import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fromUserModel } from '../../mappers/users/toUserUI';
import { DashboardPage } from '../../ui/dashboard/DashboardPage';
import { IconKebab } from '../../ui/icon/IconKebab';
import { IconButton } from '../../ui/input/IconButton';
import { Box } from '../../ui/layout/Box';
import { Menu, MenuItem } from '../../ui/navigation/Menu';
import { Typography } from '../../ui/typography/Typography';
import { UserCard, UserCardSkeleton } from '../../ui/users/UserCard';
import { canNotRegisterBusiness } from '../../utils/accessService';
import { ymTrackGoToRegistration } from '../../utils/metrika';
import { useBackButton } from '../../utils/useBackButton';
import { useMeta } from '../../utils/useMeta';
import { useTranslation } from '../../utils/useTranslation';
import { DialogRoute, useOpenDialog } from '../ModalRoute';
import { DIALOGS } from '../constants';
import { withAuthProtection } from '../withAuthProtection';
import { UserCollections } from './UserCollections';
import { UserSubscriptions } from './UserSubscriptions';
import { useCurrentUser } from './useCurrentUser';

const USER_AGREEMENT_LINK = 'https://yandex.ru/legal/ritm_termsofuse/';

export const UserDetails = withAuthProtection(() => {
    const openDialog = useOpenDialog();
    const currentUser = useCurrentUser();
    const onNavigateBack = useBackButton();
    const navigate = useNavigate();
    const t = useTranslation();
    const currentUserUI = currentUser ? fromUserModel(currentUser) : undefined;

    useMeta(
        currentUser
            ? { title: `${currentUser.displayName ?? currentUser.login} • Яндекс Ритм` }
            : undefined,
    );

    const onSupportClick = useCallback(() => {
        window.open(
            `${process.env.REACT_APP_BETA_SUPPORT_FORM_REGULAR_LINK}?login=${currentUser?.login}`,
            '_blank',
        );
    }, [currentUser?.login]);

    const onMoveToRegistration = useCallback(() => {
        ymTrackGoToRegistration({
            page: 'Профиль пользователя',
            userLogin: currentUser?.login,
        });
        window.open(`${process.env.REACT_APP_REGISTRATION_LINK}`);
    }, [currentUser?.login, navigate]);

    return (
        <DashboardPage py={0}>
            <Box
                width="100%"
                minHeight="100%"
                position="relative"
                background={{ xs: 'var(--background-primary)', sm: 'none' }}
            >
                <Box zIndex={10} position="absolute" top={0} right={0}>
                    <Menu
                        pt={1}
                        pr={1}
                        trigger={
                            <IconButton>
                                <IconKebab />
                            </IconButton>
                        }
                    >
                        <MenuItem onClick={() => window.open(USER_AGREEMENT_LINK, '_blank')}>
                            <Typography>{t('Пользовательское соглашение')}</Typography>
                        </MenuItem>
                        <MenuItem onClick={onSupportClick}>
                            <Typography>{t('Поддержка')}</Typography>
                        </MenuItem>
                        {!currentUser ||
                        canNotRegisterBusiness(
                            currentUser.businessRegistrationStatus,
                        ) ? undefined : (
                            <MenuItem onClick={onMoveToRegistration}>
                                <Typography>{t('Заявка в бету')}</Typography>
                            </MenuItem>
                        )}
                    </Menu>
                </Box>
                <Box mb={{ xs: 1, sm: 2 }}>
                    {currentUserUI ? (
                        <UserCard
                            user={currentUserUI}
                            onShowSubscriptions={() => openDialog(DIALOGS.SUBSCRIPTIONS)}
                        />
                    ) : (
                        <UserCardSkeleton />
                    )}
                </Box>
                <UserCollections />
                <DialogRoute
                    name={DIALOGS.SUBSCRIPTIONS}
                    element={<UserSubscriptions onClose={onNavigateBack} />}
                />
            </Box>
        </DashboardPage>
    );
});
